// src/format.js
// Faithful port of vmtvw_format_result() (../visamundi-widget/inc/render.php) and the
// visamundi_url agency/affilae logic (inc/functions/api.php), plus the
// accent-insensitive country sort (vmtvw_get_countries).
import { t } from './i18n.js';

const REQUIREMENT_KEYS = {
  'visa required': 'reqVisaRequired',
  'visa on arrival': 'reqVisaOnArrival',
  'e-visa': 'reqEvisa',
  // eta/esta are electronic travel authorisations — same UX as an e-visa. Mapping
  // them here closes the gap where they fell through to the "no information" branch.
  eta: 'reqEvisa',
  esta: 'reqEvisa',
  'Hayya Entry Permit': 'reqHayya',
  'visa free': 'reqVisaFree',
  'no admission': 'reqNoAdmission',
};

// Status badge per requirement: label key (i18n) + tone (drives the badge colour).
const STATUS = {
  'visa required': { key: 'stRequired', tone: 'warn' },
  'visa on arrival': { key: 'stArrival', tone: 'info' },
  'e-visa': { key: 'stEvisa', tone: 'teal' },
  eta: { key: 'stEvisa', tone: 'teal' },
  esta: { key: 'stEvisa', tone: 'teal' },
  'Hayya Entry Permit': { key: 'stHayya', tone: 'teal' },
  'visa free': { key: 'stFree', tone: 'good' },
  'no admission': { key: 'stNoAdmission', tone: 'bad' },
};

// Label the Directus `requirements` collection returns when nationality and
// destination are the same country (the WP plugin used a -1 sentinel instead).
const SAME_COUNTRY_REQUIREMENT = 'Nationalité et destination identiques';

function statusFor(requirement, asInt) {
  if (Object.prototype.hasOwnProperty.call(STATUS, requirement)) return STATUS[requirement];
  if (asInt > 0) return { key: 'stFree', tone: 'good' };
  return { key: 'stUnknown', tone: 'muted' };
}

// Mirrors vmtvw_format_result(): no CTA for these outcomes.
const NO_BUTTON = new Set(['visa free', 'no admission']);

function buttonLabel(requirement, lang) {
  if (typeof requirement === 'number' && requirement > 0) return t(lang, 'getOtherDocument');
  if (requirement === 'e-visa') return t(lang, 'getDocument');
  return t(lang, 'getVisa');
}

// `opts.sameCountry` is set by the caller when both selected codes are equal.
export function formatResult(data, lang, { sameCountry = false } = {}) {
  const requirement = data.requirement;
  const asInt = parseInt(requirement, 10);

  // Destination equals passport country: no travel document needed.
  if (sameCountry || asInt === -1 || requirement === SAME_COUNTRY_REQUIREMENT) {
    return {
      status: { label: t(lang, 'stHome'), tone: 'good' },
      text: sentence(data, lang, t(lang, 'reqHome')),
      button: null,
      notified: false,
    };
  }

  let requirementPhrase = '';
  let notified = false;

  if (Object.prototype.hasOwnProperty.call(REQUIREMENT_KEYS, requirement)) {
    requirementPhrase = t(lang, REQUIREMENT_KEYS[requirement]);
  } else if (asInt > 0) {
    requirementPhrase = t(lang, 'stayDays', asInt);
  } else {
    requirementPhrase = t(lang, 'reqUnknown');
    notified = true;
  }

  const text = sentence(data, lang, requirementPhrase);

  let button = null;
  const isNumeric = asInt > 0;
  const hideButton = NO_BUTTON.has(requirement) || notified || !data.visamundi_url;
  if (!hideButton) {
    button = {
      label: buttonLabel(isNumeric ? asInt : requirement, lang),
      url: data.visamundi_url,
    };
  }

  const st = statusFor(requirement, asInt);
  const status = { label: t(lang, st.key), tone: st.tone };

  return { status, text, button, notified };
}

function sentence(data, lang, requirementPhrase) {
  return t(
    lang,
    'sentence',
    data.i18n.destination.country.prefix,
    data.i18n.destination.country.name,
    String(data.i18n.nationality.demonym).toLowerCase(),
    requirementPhrase,
  );
}

// Appends the partner identifier as ?partner=<id> to any widget-emitted URL.
// partnerId → ?partner=…; affilaeId → #<id> (Affilae tracks from the URL hash,
// like the WP plugin's affilae_id).
export function buildVisamundiUrl(url, { partnerId, affilaeId } = {}) {
  if (!url) return url;
  let [out, hash] = url.split('#');
  if (partnerId) {
    out += (out.includes('?') ? '&' : '?') + 'partner=' + encodeURIComponent(partnerId);
  }
  if (affilaeId) hash = encodeURIComponent(affilaeId);
  return hash !== undefined ? `${out}#${hash}` : out;
}

export function sortCountries(countries) {
  const strip = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');
  return [...countries].sort((a, b) =>
    strip(a.name).localeCompare(strip(b.name), 'en'),
  );
}
